import { createSlice } from '@reduxjs/toolkit';
import apiClient from '@miroculus/api-client';
const WORKSPACE_ORDER_ITEM = 'workspace-order';
export const MEMBER_ROLES = {
    admin: 'Admin',
    collaborator: 'Collaborator',
};
const initialState = {
    currentWorkspace: null,
    loadingMembers: false,
    creatingMember: false,
    devices: [],
    registeringDevice: false,
    loadingDevices: false,
    members: [],
    loadingWorkspaces: false,
    workspaces: [],
    pendingInvites: [],
};
const workspacesSlice = createSlice({
    name: 'workspaces',
    initialState,
    reducers: {
        updateWorkspaces: (state, action) => {
            const workspaces = action.payload;
            const workspaceOrder = apiClient.storage.get(WORKSPACE_ORDER_ITEM);
            if (workspaceOrder) {
                // workspace list may have changed from the one stored,
                // so we first get the available ordered workspaces
                const orderedWorkspaces = workspaceOrder
                    .map((id) => workspaces.find((w) => w.id === id))
                    .filter((w) => !!w);
                // then we get extra workspaces that the user may not have already ordered
                const extraWorkspaces = workspaces.filter((w) => !workspaceOrder.includes(w.id));
                // then we concatenate both lists
                state.workspaces = [...orderedWorkspaces, ...extraWorkspaces];
            }
            else {
                state.workspaces = workspaces;
            }
        },
        updateMembers: (state, action) => {
            state.members = action.payload;
        },
        updateLoadingWorkspaces: (state, action) => {
            state.loadingWorkspaces = action.payload;
        },
        updateLoadingMembers: (state, action) => {
            state.loadingMembers = action.payload;
        },
        updateCreatingMember: (state, action) => {
            state.creatingMember = action.payload;
        },
        updateLoadingDevices: (state, action) => {
            state.loadingDevices = action.payload;
        },
        updateDevices: (state, action) => {
            state.devices = action.payload;
        },
        updateRegisteringDevice: (state, action) => {
            state.registeringDevice = action.payload;
        },
        updatePendingInvites: (state, action) => {
            state.pendingInvites = action.payload;
        },
    },
});
// Export actions
export const { updateWorkspaces, updateMembers, updateLoadingWorkspaces, updateLoadingMembers, updateCreatingMember, updateLoadingDevices, updateDevices, updateRegisteringDevice, updatePendingInvites, } = workspacesSlice.actions;
// Thunk Actions
export const loadWorkspaceMembers = () => async (dispatch) => {
    dispatch(updateLoadingMembers(true));
    try {
        const { body } = await apiClient.get('/team-members');
        const members = body.map(({ id, role, user, active }) => ({
            id,
            role,
            active,
            name: user.name,
            email: user.email,
        }));
        dispatch(updateMembers(members));
    }
    catch (e) {
        console.error(e);
    }
    dispatch(updateLoadingMembers(false));
};
export const updateWorkspaceMember = ({ id, ...values }) => async (dispatch, getState) => {
    const { members } = getState().workspaces;
    const memberIndex = members.findIndex((m) => m.id === id);
    await apiClient.post(`/team-members/${id}`, values);
    dispatch(updateMembers([
        ...members.slice(0, memberIndex),
        {
            ...members[memberIndex],
            ...values,
        },
        ...members.slice(memberIndex + 1),
    ]));
};
export const updateWorkspaceOrder = (workspaces) => (dispatch, getState) => {
    apiClient.storage.set(WORKSPACE_ORDER_ITEM, workspaces.map(({ id }) => id));
    // call updateWorkspaces action to reorder the store state
    dispatch(updateWorkspaces(getState().workspaces.workspaces));
};
export const loadPendingInvites = () => async (dispatch) => {
    try {
        const { body } = await apiClient.get('/invitations');
        const invites = body.map(({ id, email, role }) => ({
            id,
            email,
            role,
        }));
        dispatch(updatePendingInvites(invites));
    }
    catch (e) {
        console.error(e);
    }
};
export const removeInvite = (id) => async (dispatch) => {
    try {
        await apiClient.delete(`/invitations/${id}`);
    }
    catch (e) {
        console.error(e);
    }
};
export const inviteUserToWorkspace = (email, role) => async (dispatch) => {
    dispatch(updateCreatingMember(true));
    try {
        await apiClient.post('/invitations', { email, role });
    }
    catch (e) {
        console.error(e);
        throw e;
    }
    finally {
        dispatch(updateCreatingMember(false));
    }
};
// Export reducer as default
export default workspacesSlice.reducer;
